import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  putApiIntegrationsProductItemsEnableSync,
  putApiIntegrationsProductItemsPause,
  type IntegrationProvider,
  type ProductItemIntegrationSyncStatusRequest,
} from "apiClients/base-generated";
import { getHeyApiRequestOptions, normalizeHeyApiLang } from "apiClients/base/heyApiRequestOptions";

function heyApiResultToError(err: unknown): Error {
  const message = typeof err === "string" ? err : JSON.stringify(err);
  return new Error(message);
}

export type IntegrationProductsByProductIdsVariables = {
  integrationProvider: IntegrationProvider;
  productItemIds: number[];
};

function buildBody(vars: IntegrationProductsByProductIdsVariables): ProductItemIntegrationSyncStatusRequest {
  return {
    integrationProvider: vars.integrationProvider,
    productItemIds: vars.productItemIds,
  };
}

export function useIntegrationProductsPauseMutation(locale: string | null | undefined) {
  const queryClient = useQueryClient();
  const lang = normalizeHeyApiLang(locale);

  return useMutation({
    mutationFn: async (vars: IntegrationProductsByProductIdsVariables) => {
      if (!vars.productItemIds.length) {
        throw new Error("Inga produkt-ID:n.");
      }
      const result = await putApiIntegrationsProductItemsPause({
        ...getHeyApiRequestOptions(lang),
        body: buildBody(vars),
      });
      if (result.error) {
        throw heyApiResultToError(result.error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-products"] });
    },
  });
}

export function useIntegrationProductsEnableSyncMutation(locale: string | null | undefined) {
  const queryClient = useQueryClient();
  const lang = normalizeHeyApiLang(locale);

  return useMutation({
    mutationFn: async (vars: IntegrationProductsByProductIdsVariables) => {
      if (!vars.productItemIds.length) {
        throw new Error("Inga produkt-ID:n.");
      }
      const result = await putApiIntegrationsProductItemsEnableSync({
        ...getHeyApiRequestOptions(lang),
        body: buildBody(vars),
      });
      if (result.error) {
        throw heyApiResultToError(result.error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-products"] });
    },
  });
}
